import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import * as z from 'zod';
import { IconButton, Tooltip } from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ClienteDialog from '../components/ClienteDialog';
import { useDiasEspeciales } from '../hooks/useDiasEspeciales';
import {
  Stack,
  Grid,
  TextField,
  MenuItem,
  Checkbox,
  FormGroup,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Autocomplete,
  Typography,
  RadioGroup,
  Radio,
  FormLabel,
} from '@mui/material';
import Paper, { PaperProps } from '@mui/material/Paper';

import { isAxiosError } from 'axios';
import { useSnackbar } from 'notistack';

import { useReservaMutation } from '../hooks/useReservationMutation';
import LoadingButton from './LoadingButton';
import api from '../api/axios';
import { CURSOS, ESTADOS_RESERVA, ESTADOS_CANCELLED } from '../constants/enums';
import { useCentrosProfesor } from '../hooks/useCentrosProfesor';
import { useFormatosProfesor } from '../hooks/useFormatosProfesor';
import { useAuth } from '../context/AuthContext';
import { useProyectos } from '../hooks/useProyectos';

/* === Draggable === */
import Draggable from 'react-draggable';

/* === SAD dialogs & hooks === */
import SadHorasDialog from '../components/sad/SadHorasDialog';
import SadCreateDialog from '../components/sad/SadCreateDialog';
import { useSad, CrearSadPayload } from '../hooks/useSad';
import { SAD_FEATURE_ENABLED } from '../config/features';
/* === Store de reintentos === */
import { saveSadRetryAttempt } from '../utils/sadRetryStore';

/* === auto-asignación inmediata + datos de aulas para asignación manual === */
import {
  useAsignacionAuto,
  useAulasByCentro,
  usePlanificadorCentro,
  useAsignacionManual,
  useAsignacionManualPropagar,
} from '../api/aulas';

/* ⭐️ NUEVO: invalidación de queries (React Query) */
import { useQueryClient } from '@tanstack/react-query';

/* ═════════ helpers / validación ═════════ */
const toNumber = (v: unknown) =>
  v === '' || v === undefined ? undefined : typeof v === 'string' ? Number(v) : v;

const looksLikeCode = (v: unknown): v is string =>
  typeof v === 'string' && /^[A-Z0-9_]+$/.test(v.trim());

/** Normaliza valores típicos que pueden venir de MySQL (TINYINT, strings, etc.) */
const isActiveTrue = (v: any) => {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (['1', 'true', 'si', 'sí', 'activo', 'active', 'enabled', 'yes'].includes(s)) return true;
    if (['0', 'false', 'no', 'inactivo', 'inactive', 'baja', 'archivado', 'disabled'].includes(s))
      return false;
  }
  return !!v;
};

const hhmmToMinutes = (value?: string | null): number | null => {
  if (!value) return null;
  const raw = String(value).slice(0, 5);
  if (!/^\d{2}:\d{2}$/.test(raw)) return null;
  const [hh, mm] = raw.split(':').map(Number);
  return hh * 60 + mm;
};

const formatoIdToMask = (id?: number | null): number => {
  if (id === 1) return 2; // online
  if (id === 2) return 1; // presencial
  if (id === 3) return 4; // domicilio
  if (id === 4) return 7; // todos
  return 0;
};

/* ─────── esquema DTO ─────── */
const base = z.object({
  idProfesor: z.number(),
  idAsignatura: z.number(),
  idFormato: z.number(),
  fecha: z.string().min(10),
  horaInicio: z.string().regex(/^[0-9]{2}:[0-9]{2}$/),
  horaFin: z.string().regex(/^[0-9]{2}:[0-9]{2}$/),
  idProyecto: z.number(),
  language: z.enum(['English', 'Spanish']),

  /* opcionales */
  idCentro: z.number().optional(),
  curso: z.enum(CURSOS).optional(),
  examinationBoard: z.string().optional(),
  linkAddress: z.string().optional(),
  comentarios: z.string().optional(),
  comentariosInternos: z.string().optional(),
  estado: z
    .string()
    .optional()
    .refine((v) => !v || (ESTADOS_RESERVA as readonly string[]).includes(v), 'Estado no válido'),

  /* incidencia */
  incidencia: z.boolean().optional(),
  /* shadowing */
  shadowing: z.boolean().optional().default(false),
  idProfesorShadow: z.number().optional(),

  /* grupos */
  esGrupal: z.boolean().default(false),
  numAlumnos: z.preprocess(toNumber, z.number().int().min(1, 'Debe ser ≥1')),
  alumnosSel: z.array(z.number()).optional(),
});

const simpleSchema = base.extend({ recurre: z.literal(false) });
const recurSchema = base.extend({
  recurre: z.literal(true),
  patron: z.enum(['WEEKLY', 'MONTHLY', 'DAILY5']),
  diasSemana: z.array(z.number().int().min(0).max(6)).optional(),
  intervalo: z.preprocess(toNumber, z.number().int().min(1)),
  repeticiones: z.preprocess(toNumber, z.number().int().min(1)).optional(),
});

const schema = z.union([simpleSchema, recurSchema]).superRefine((data, ctx) => {
  const toMins = (s?: string) => {
    if (!s || !/^\d{2}:\d{2}$/.test(s)) return null;
    const [hh, mm] = s.split(':').map(Number);
    return hh * 60 + mm;
  };
  const ini = toMins(data.horaInicio);
  const fin = toMins(data.horaFin);
  if (ini != null && fin != null && ini >= fin) {
    ctx.addIssue({
      path: ['horaInicio'],
      code: z.ZodIssueCode.custom,
      message: 'La hora de inicio debe ser anterior a la hora de fin',
    });
    ctx.addIssue({
      path: ['horaFin'],
      code: z.ZodIssueCode.custom,
      message: 'La hora de fin debe ser posterior a la hora de inicio',
    });
  }

  const cancelled = !!data.estado && (ESTADOS_CANCELLED as readonly string[]).includes(data.estado);
  if ((cancelled || data.incidencia) && !data.comentariosInternos?.trim()) {
    ctx.addIssue({
      path: ['comentariosInternos'],
      code: z.ZodIssueCode.custom,
      message: cancelled ? 'Explica el motivo de la cancelación' : 'Describe la incidencia',
    });
  }
  // Shadowing: si está marcado, debe venir un segundo profesor distinto
  if (data.shadowing) {
    if (!data.idProfesorShadow) {
      ctx.addIssue({
        path: ['idProfesorShadow'],
        code: z.ZodIssueCode.custom,
        message: 'Selecciona un segundo profesor cuando Shadowing está activo',
      });
    } else if (data.idProfesorShadow === data.idProfesor) {
      ctx.addIssue({
        path: ['idProfesorShadow'],
        code: z.ZodIssueCode.custom,
        message: 'El segundo profesor debe ser distinto al primero',
      });
    }
  }
});

/* ─────── Tipos y props ─────── */
export type ReservationFormData = z.infer<typeof schema>;
export type Mode = 'create' | 'edit';

interface Props {
  mode: Mode;
  initialValues?: Partial<ReservationFormData> & {
    idClienteUsr?: number;
    alumnosIdsUsr?: number[];
    idCliente?: number;
    alumnosIds?: number[];
    idRecurrencia?: string;
    nombreProf?: string;
  };
  reservaId?: number;
  onSaved?: () => void;
  onCancel?: () => void;
}

/* ─────── PaperComponent draggable ─────── */
const DraggablePaper = React.forwardRef<HTMLDivElement, PaperProps>(
  function DraggablePaper(props, ref) {
    const nodeRef = React.useRef<HTMLDivElement>(null!);
    React.useImperativeHandle(ref, () => nodeRef.current, []);
    return (
      <Draggable
        nodeRef={nodeRef}
        handle='[class*="MuiDialogTitle-root"]'
        cancel='[class*="MuiDialogContent-root"], [class*="MuiAutocomplete-root"]'
        bounds="body"
      >
        <Paper
          ref={nodeRef}
          {...props}
          sx={{
            ...props?.sx,
            resize: 'both',
            overflow: 'auto',
            maxWidth: '90vw',
            maxHeight: '90vh',
          }}
        />
      </Draggable>
    );
  },
);

/* ═════════ helpers de detección de errores ═════════ */
function packErrorText(payload: any): string {
  const parts = [
    payload?.message,
    payload?.error,
    payload?.msg,
    payload?.detail,
    payload?.details,
    payload?.title,
  ]
    .filter(Boolean)
    .map(String);
  return parts.join(' ').toLowerCase();
}

function isWeeklyLimitError(err: unknown): boolean {
  if (!isAxiosError(err)) return false;
  const res = err.response;
  const payload: any = res?.data ?? {};
  const code = String(payload?.code || payload?.errorCode || '').toUpperCase();
  const text = packErrorText(payload);
  return res?.status === 409 && (code === 'WEEKLY_LIMIT' || /horas\s*semanales/.test(text));
}

function isNoAvailabilityError(err: unknown): boolean {
  if (!isAxiosError(err)) return false;
  const res = err.response;
  const payload: any = res?.data ?? {};
  const code = String(payload?.code || payload?.errorCode || '').toUpperCase();
  const text = packErrorText(payload);

  const codeHit = [
    'NO_DISPONIBILIDAD',
    'NO_AVAILABILITY',
    'TEACHER_NOT_AVAILABLE',
    'TEACHER_NOT_AVAILABLE_FOR_SLOT',
    'NO_AVAIL',
    'NO_SLOT',
    'NO_GAP',
  ].includes(code);

  const textHit =
    /profesor\s+sin\s+disponibilidad/i.test(text) ||
    /sin\s+disponibilidad\s+horaria/i.test(text) ||
    /para\s+ese\s+tramo/i.test(text) ||
    /no\s+hay\s+disponibilidad/i.test(text) ||
    /not\s+available/i.test(text) ||
    (/teacher|profesor|docente/.test(text) && /(no|sin)\s+(?:disponibilidad|available)/.test(text));

  const statusHit = [409, 422, 400].includes(res?.status ?? 0);

  return statusHit && (codeHit || textHit);
}

/** error cuando la serie supera el 30% de pendientes. */
function isPendingLimitError(err: unknown): boolean {
  if (!isAxiosError(err)) return false;
  const res = err.response;
  const payload: any = res?.data ?? {};
  const code = String(payload?.code || payload?.errorCode || '').toUpperCase();
  const text = packErrorText(payload);
  const statusHit = [409, 400].includes(res?.status ?? 0);
  return (
    statusHit &&
    (code === 'PENDING_LIMIT' || (/%/.test(text) && /pendiente\s*de\s*reagendar/i.test(text)))
  );
}

function isInactiveTeacherError(err: unknown): boolean {
  if (!isAxiosError(err)) return false;
  const res = err.response;
  const payload: any = res?.data ?? {};
  const text = packErrorText(payload);
  const statusHit = [409, 400].includes(res?.status ?? 0);

  const explicitMsgHit =
    /profesor\s+est[aá]\s+inactivo/i.test(text) ||
    /no\s+se\s+puede\s+(agendar|cambiar\s+a\s*confirmed)/i.test(text);

  const genericHit =
    /profesor|docente|teacher/.test(text) && /inactivo|inactive|disabled|deactivated/.test(text);

  return statusHit && (explicitMsgHit || genericHit);
}

const recurrenceExceedsTwoWeeks = (
  start?: string,
  end?: string,
  fallbackDays?: number,
): boolean => {
  if (start && end) {
    return dayjs(end).diff(dayjs(start), 'day') > 13;
  }
  if (fallbackDays !== undefined && Number.isFinite(fallbackDays)) {
    return fallbackDays > 14;
  }
  return false;
};

/* ═════════ COMPONENTE ═════════ */
export default function ReservationForm({
  mode,
  initialValues = {},
  reservaId,
  onSaved,
  onCancel,
}: Props) {
  const { t } = useTranslation<'translation'>();

  /* ── permisos ── */
  const { user } = useAuth();
  const isProfesor = user?.roles?.includes('profesor');
  const isCliente = user?.roles?.includes('cliente');
  const showInternos = !(isProfesor || isCliente);

  /* ── snackbar ── */
  const { enqueueSnackbar } = useSnackbar();
  const { rows: proyectos = [] } = useProyectos({ activos: true });

  /* ⭐️ NUEVO: queryClient para invalidaciones */
  const queryClient = useQueryClient();

  /* ⭐️ NUEVO: helper para avisar a otros componentes (ReservaDialog) */
  const notifyReservaUpdated = useCallback((id?: number) => {
    if (!id) return;
    try {
      window.dispatchEvent(new CustomEvent('reserva:updated', { detail: { id } }));
    } catch {
      /* no-op */
    }
  }, []);

  /* ── 1. normalizar props ── */
  const normalizedInitial = useMemo(() => {
    const iv = initialValues as any;
    const alumnosInicial =
      (iv.idClienteUsr ?? iv.idCliente) || (iv.alumnosIdsUsr ?? iv.alumnosIds)?.length
        ? [iv.idClienteUsr ?? iv.idCliente, ...(iv.alumnosIdsUsr ?? iv.alumnosIds ?? [])].filter(
            Boolean,
          )
        : [];
    const esGrupalDetect = (iv.numAlumnos ?? 1) > 1;
    const trim = (t?: string) => (t?.length === 8 ? t.slice(0, 5) : t);
    return {
      ...iv,
      alumnosSel: iv.alumnosSel ?? alumnosInicial,
      fecha: iv.fecha ? dayjs(iv.fecha).format('YYYY-MM-DD') : undefined,
      horaInicio: trim(iv.horaInicio),
      horaFin: trim(iv.horaFin),
      recurre: false,
      esGrupal: esGrupalDetect,
      numAlumnos: iv.numAlumnos ?? 1,
      incidencia: iv.incidencia ?? false,
      comentariosInternos: iv.comentariosInternos ?? '',
      scopeSeries: 'current',
    } as Partial<ReservationFormData & { scopeSeries: string }>;
  }, [initialValues]);

  const jsDayToDb = (d: number) => (d + 6) % 7;

  const DEFAULT_ESTADO: string | undefined = useMemo(() => {
    const arr = ESTADOS_RESERVA as readonly string[];
    return arr.find((s) => s.toLowerCase().startsWith('confirmed')) ?? arr[0] ?? undefined;
  }, []);

  const defaultValues = useMemo(() => {
    const baseDate = normalizedInitial.fecha ?? dayjs().format('YYYY-MM-DD');
    return {
      ...normalizedInitial,
      idProyecto: (normalizedInitial as any).idProyecto ?? undefined,
      language: normalizedInitial.language ?? 'English',
      fecha: normalizedInitial.fecha ?? baseDate,
      horaInicio: normalizedInitial.horaInicio ?? '16:00',
      horaFin: normalizedInitial.horaFin ?? '17:00',
      estado: normalizedInitial.estado ?? DEFAULT_ESTADO,
      incidencia: normalizedInitial.incidencia ?? false,
      scopeSeries: (normalizedInitial as any).scopeSeries ?? 'current',
      recurre: (normalizedInitial as any).recurre ?? false,
      patron: (normalizedInitial as any).patron ?? 'WEEKLY',
      intervalo: (normalizedInitial as any).intervalo ?? 1,
      diasSemana: (normalizedInitial as any).diasSemana ?? [jsDayToDb(dayjs(baseDate).day())],
    } as Partial<ReservationFormData & { scopeSeries: string }>;
  }, [normalizedInitial, DEFAULT_ESTADO]);

  /* ── 2. react-hook-form ── */
  const {
    watch,
    reset,
    handleSubmit,
    control,
    setValue,
    formState: { isSubmitting, errors },
  } = useForm<ReservationFormData & { scopeSeries: string }>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues: defaultValues as any,
  });

  const [clienteDlgOpen, setClienteDlgOpen] = useState(false);

  const reloadUsuarios = useCallback(async () => {
    const { data } = await api.get('/usuarios', { params: { activo: true } });
    setUsuariosRaw(data);
  }, []);

  /* Mantiene lo tecleado al refrescar defaults */
  useEffect(() => {
    reset(defaultValues as any, { keepDirtyValues: true });
  }, [defaultValues, reset]);

  /* ── 3. flags observados ── */
  const esGrupal = watch('esGrupal');
  const numAlumnos = watch('numAlumnos');
  const alumnosSel = watch('alumnosSel');
  const scopeSeries = watch('scopeSeries');
  const firstRun = useRef(true);

  /* preferencia de auto-asignación */
  const [autoAssignOnSave, setAutoAssignOnSave] = useState(true);

  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    if (!esGrupal) {
      if (numAlumnos !== 1) setValue('numAlumnos', 1);
      if (Array.isArray(alumnosSel) && alumnosSel.length > 1)
        setValue('alumnosSel', [alumnosSel[0]]);
    }
  }, [esGrupal, numAlumnos, alumnosSel, setValue]);

  /* ── 4. catálogos ── */
  const [usuariosRaw, setUsuariosRaw] = useState<any[]>([]);
  const [clientes, setClientes] = useState<any[]>([]);
  const [asignaturas, setAsignaturas] = useState<any[]>([]);
  const [profesoresDb, setProfesores] = useState<any[]>([]);
  const [loadingProfesores, setLoadingProfesores] = useState(false);
  const [formatos, setFormatos] = useState<any[]>([]);
  const [centrosAll, setCentrosAll] = useState<any[]>([]);
  const [loadingCats, setLoadingCats] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/usuarios', { params: { activo: true } }).then((r) => setUsuariosRaw(r.data)),
      api.get('/asignaturas').then((r) => setAsignaturas(r.data)),
      api.get('/formatos').then((r) => setFormatos(r.data)),
      api.get('/centros').then((r) => setCentrosAll(r.data)),
    ]).finally(() => setLoadingCats(false));
  }, []);

  // Solo CLIENTES ACTIVOS en opciones (manteniendo extras ya seleccionados)
  useEffect(() => {
    const soloClientesActivos = usuariosRaw.filter(
      (u: any) => u.rol === 'cliente' && isActiveTrue(u.activo),
    );
    const extras = (Array.isArray(alumnosSel) ? alumnosSel : [])
      .filter((id: number) => !soloClientesActivos.some((c: any) => c.id === id))
      .map((id) => ({ id, nombreCompleto: `#${id}` }));
    setClientes([...soloClientesActivos, ...extras]);
  }, [usuariosRaw, alumnosSel]);

  const idAsignatura = watch('idAsignatura');
  const asignNoRequiere = useMemo(() => {
    const a = asignaturas.find((x: any) => Number(x.id) === Number(idAsignatura));
    return !!(
      a &&
      (a.noRequiereAlumno === true || a.noRequiereAlumno === '1' || a.noRequiereAlumno === 1)
    );
  }, [asignaturas, idAsignatura]);
  useEffect(() => {
    if (!idAsignatura) {
      setProfesores([]);
      setLoadingProfesores(false);
      return;
    }

    let cancelled = false;
    setLoadingProfesores(true);

    api
      .get('/profesores', {
        params: { asignatura: idAsignatura, activo: true },
      })
      .then((r) => {
        if (cancelled) return;
        const data = Array.isArray(r.data) ? r.data : [];
        const activos = data.filter((p: any) => isActiveTrue(p.activo));
        setProfesores(activos);
      })
      .finally(() => {
        if (!cancelled) setLoadingProfesores(false);
      });

    return () => {
      cancelled = true;
    };
  }, [idAsignatura]);

  const selectedMainProfesorId = Number(watch('idProfesor')) || undefined;
  const selectedShadowProfesorId = Number(watch('idProfesorShadow')) || undefined;

  // Mantener profesores seleccionados aunque no estén en el listado activo para que el
  // autocomplete pueda mostrarlos correctamente al precargar (p. ej. "Agendar más").
  const profesores = useMemo(() => {
    const iv = initialValues as any;
    const idProfesorFromInitial =
      Number(iv.idProfesor ?? iv.id_profesor ?? iv.profesorId) || undefined;
    const idProfesorShadowFromInitial =
      Number(iv.idProfesorShadow ?? iv.id_profesor_shadow ?? iv.shadowingInfo?.profesor?.id) ||
      undefined;

    const idMain = selectedMainProfesorId ?? idProfesorFromInitial;
    const idShadow = selectedShadowProfesorId ?? idProfesorShadowFromInitial;

    if (!idMain && !idShadow) return profesoresDb;

    const extras: any[] = [];
    if (idMain && !profesoresDb.some((p) => Number(p.id) === Number(idMain))) {
      extras.push({ id: idMain, nombre: iv.nombreProf ?? iv.nombreProfesor ?? `#${idMain}` });
    }

    if (idShadow && !profesoresDb.some((p) => Number(p.id) === Number(idShadow))) {
      const sh = iv.shadowingInfo?.profesor ?? iv.shadowing?.profesor;
      const nombre =
        sh && (sh.nombre || sh.apellido)
          ? `${sh.nombre ?? ''} ${sh.apellido ?? ''}`.trim()
          : (sh?.nombreCompleto ?? iv.shadowingNombre ?? `#${idShadow}`);
      extras.push({ id: idShadow, nombre });
    }

    return extras.length ? [...extras, ...profesoresDb] : profesoresDb;
  }, [profesoresDb, initialValues, selectedMainProfesorId, selectedShadowProfesorId]);

  // En modo creación, si por initialValues quedó un profesor fuera del catálogo activo,
  // limpiamos el id para forzar una selección válida.
  useEffect(() => {
    if (mode !== 'create') return;
    if (!idAsignatura || loadingProfesores) return;

    const currentMain = Number(watch('idProfesor'));
    if (currentMain && !profesores.some((p) => Number(p.id) === currentMain)) {
      setValue('idProfesor', undefined as any, { shouldDirty: true });
    }

    const currentShadow = Number(watch('idProfesorShadow'));
    if (currentShadow && !profesores.some((p) => Number(p.id) === currentShadow)) {
      setValue('idProfesorShadow', undefined as any, { shouldDirty: true });
    }
  }, [mode, profesores, idAsignatura, loadingProfesores, setValue, watch]);

  // Precargar campos de shadowing desde initialValues (edición)
  useEffect(() => {
    if (!initialValues) return;
    const iv = initialValues as any;
    const hasShadowing = !!(iv.shadowingInfo || iv.shadowing || iv.idProfesorShadow);
    if (!hasShadowing) return;

    // marcar el checkbox de shadowing sin marcar el formulario como dirty
    setValue('shadowing', true, { shouldDirty: false });

    const shadowingInfo = iv.shadowingInfo || iv.shadowing;
    const shadowId = shadowingInfo?.profesor?.id ?? iv.idProfesorShadow;
    if (shadowId) {
      setValue('idProfesorShadow', Number(shadowId), { shouldDirty: false });
    }
  }, [initialValues, setValue]);

  const idProfesor = watch('idProfesor') || 0;
  const { rows: centrosProfesor, loading: loadingCtr } = useCentrosProfesor(idProfesor);
  const { rows: formatosProfesor, loading: loadingFmt } = useFormatosProfesor(idProfesor);

  const recurre = useWatch({ control, name: 'recurre' });
  const patron = useWatch({ control, name: 'patron' });
  const diasSemanaSel = useWatch({ control, name: 'diasSemana' });
  const fechaSel = watch('fecha') ?? dayjs().format('YYYY-MM-DD');

  // Rango: curso académico del año de la fecha seleccionada
  const startYear =
    dayjs(fechaSel).month() >= 8 ? dayjs(fechaSel).year() : dayjs(fechaSel).year() - 1;
  const desde = `${startYear}-09-01`;
  const hasta = `${startYear + 1}-08-31`;
  const { data: diasEsp = [] } = useDiasEspeciales(idProfesor, desde, hasta);
  const idFormatoSel = Number(watch('idFormato')) || undefined;
  const horaInicioForm = watch('horaInicio') ?? '';
  const horaFinForm = watch('horaFin') ?? '';
  const idCentroSeleccionado = watch('idCentro');

  useEffect(() => {
    if (recurre && patron === 'WEEKLY' && (!diasSemanaSel || diasSemanaSel.length === 0)) {
      setValue('diasSemana', [jsDayToDb(dayjs(fechaSel).day())], {
        shouldDirty: false,
      });
    }
  }, [recurre, patron, fechaSel, diasSemanaSel, setValue]);

  const centrosEspecialesTramo = useMemo(() => {
    const ini = hhmmToMinutes(horaInicioForm);
    const fin = hhmmToMinutes(horaFinForm);
    if (ini == null || fin == null || ini >= fin) return [] as number[];

    const formatoMask = formatoIdToMask(idFormatoSel ?? null);
    const ids = new Set<number>();

    for (const esp of diasEsp) {
      if (esp.fecha !== fechaSel || esp.bloqueo || esp.idCentro == null) continue;
      if (formatoMask !== 0 && ((esp.formatosMask ?? 7) & formatoMask) === 0) continue;

      const espIni = hhmmToMinutes(esp.horaInicio);
      const espFin = hhmmToMinutes(esp.horaFin);
      if (espIni == null || espFin == null) continue;
      if (ini < espFin && fin > espIni) {
        ids.add(Number(esp.idCentro));
      }
    }

    return Array.from(ids);
  }, [diasEsp, fechaSel, horaInicioForm, horaFinForm, idFormatoSel]);

  const centrosVisibles = useMemo(() => {
    const base = idProfesor ? centrosProfesor : centrosAll;

    const map = new Map<number, any>(base.map((c) => [Number(c.id), c]));

    for (const centroId of centrosEspecialesTramo) {
      const extra = centrosAll.find((c) => Number(c.id) === Number(centroId));
      if (extra) map.set(Number(extra.id), extra);
    }

    if (idCentroSeleccionado != null && !map.has(Number(idCentroSeleccionado))) {
      const seleccionado = centrosAll.find((c) => Number(c.id) === Number(idCentroSeleccionado));
      if (seleccionado) map.set(Number(seleccionado.id), seleccionado);
    }

    return Array.from(map.values());
  }, [idProfesor, centrosProfesor, centrosAll, centrosEspecialesTramo, idCentroSeleccionado]);

  const formatosVisibles = useMemo(() => {
    if (!idProfesor) return formatos;
    const tieneTodos = formatosProfesor.some((f) => f.nombreFormato === 'Todos');
    return tieneTodos ? formatos.filter((f) => f.nombreFormato !== 'Todos') : formatosProfesor;
  }, [idProfesor, formatosProfesor, formatos]);

  /* ── 5. submit ── */
  const { mutateAsync } = useReservaMutation();

  /* hooks aula */
  const autoAssign = useAsignacionAuto();
  const asignManual = useAsignacionManual();
  const asignManualProp = useAsignacionManualPropagar();

  /* estado para asignación manual */
  const centroIdSel = watch('idCentro') ?? 0;
  const horaInicioSel = watch('horaInicio') ?? '00:00';
  const horaFinSel = watch('horaFin') ?? '00:00';
  const [selectedAulaId, setSelectedAulaId] = useState<number | ''>('');

  // Reset selección si cambia el centro
  useEffect(() => {
    setSelectedAulaId('');
  }, [centroIdSel]);

  // Datos de aulas y ocupación/bloqueos para información contextual
  const { data: aulasCentro = [], isLoading: aulasLoading } = useAulasByCentro(centroIdSel);
  const { data: plan, isLoading: planLoading } = usePlanificadorCentro(centroIdSel, fechaSel);

  const overlaps = (h1: string, h2: string, H1: string, H2: string) => !(h2 <= H1 || h1 >= H2);
  const dow0 = useMemo(() => new Date(`${fechaSel}T00:00:00`).getDay(), [fechaSel]); // 0..6

  const ocupadasSet = useMemo(() => {
    const set = new Set<number>();
    if (!plan) return set;
    const a = (horaInicioSel?.length === 5 ? `${horaInicioSel}:00` : horaInicioSel) || '00:00:00';
    const b = (horaFinSel?.length === 5 ? `${horaFinSel}:00` : horaFinSel) || '23:59:59';
    for (const r of plan.reservas) {
      if (r.aula_estado !== 'ASIGNADA' || !r.id_aula) continue;
      if (overlaps(a, b, r.hora_inicio, r.hora_fin)) set.add(r.id_aula);
    }
    return set;
  }, [plan, horaInicioSel, horaFinSel]);

  const bloqueadasSet = useMemo(() => {
    const set = new Set<number>();
    if (!plan) return set;
    const a = (horaInicioSel?.length === 5 ? `${horaInicioSel}:00` : horaInicioSel) || '00:00:00';
    const b = (horaFinSel?.length === 5 ? `${horaFinSel}:00` : horaFinSel) || '23:59:59';
    for (const bl of plan.bloqueos) {
      const sameDay =
        bl.activo && bl.dow === dow0 && fechaSel >= bl.fecha_inicio && fechaSel <= bl.fecha_fin;
      if (sameDay && overlaps(a, b, bl.hora_inicio, bl.hora_fin)) {
        set.add(bl.id_aula);
      }
    }
    return set;
  }, [plan, fechaSel, dow0, horaInicioSel, horaFinSel]);

  const infoForAula = useCallback(
    (id: number) => {
      if (bloqueadasSet.has(id))
        return t('aulas.info.bloqueada', {
          defaultValue: 'Bloqueada en ese tramo (no se permite seleccionar)',
        });
      if (ocupadasSet.has(id))
        return t('aulas.info.ocupada', {
          defaultValue: 'Ocupada en ese tramo (se reorganizará para liberarla, si procede)',
        });
      return null;
    },
    [bloqueadasSet, ocupadasSet, t],
  );

  /* onInvalid */
  const onInvalid = useCallback(
    (errors: Record<string, any>) => {
      const first = Object.values(errors)[0] as any;
      const msg =
        typeof first?.message === 'string'
          ? first.message
          : t('reservationForm.error.unexpected', { defaultValue: 'Error' });

      enqueueSnackbar(msg, { variant: 'error' });
    },
    [enqueueSnackbar, t],
  );

  /* SAD horas */
  const [sadHorasOpen, setSadHorasOpen] = useState(false);
  const [sadHorasInitial, setSadHorasInitial] = useState<{
    profesorUserId: number;
    semanaLunes?: string;
    horasSolicitadas?: number;
    proyectoId?: number;
  } | null>(null);

  /* SAD disponibilidad (puntual/periodico) */
  const { create: createSad, send: sendSad } = useSad();
  const [sadDispOpen, setSadDispOpen] = useState(false);
  const [sadDispInitial, setSadDispInitial] = useState<
    (Partial<CrearSadPayload> & { profesorUserId: number }) | null
  >(null);

  /* snapshot del último intento (para reintento asistido) */
  const lastAttemptRef = useRef<any>(null);

  /* ───────── helpers auto-assign (backoff) ───────── */
  const shouldRetryLock = (err: unknown) => {
    if (!isAxiosError(err)) return false;
    const payload: any = err.response?.data ?? {};
    const text = `${payload?.sqlMessage ?? ''} ${payload?.message ?? ''}`;
    const code = String(payload?.code ?? '');
    return /Lock wait timeout/i.test(text) || code.includes('ER_LOCK_WAIT_TIMEOUT');
  };

  const runAutoAssignWithBackoff = useCallback(
    (id: number) => {
      const centroId = watch('idCentro');
      if (!autoAssignOnSave || !centroId) return;

      const attempt = (n: number) => {
        const delay = n === 0 ? 250 : 700 * n;
        setTimeout(async () => {
          try {
            await autoAssign.mutateAsync(id as number);

            /* ⭐️ invalidar caches + avisar diálogo */
            try {
              const fecha = watch('fecha') as string | undefined;
              if (fecha && centroId) {
                queryClient.invalidateQueries({
                  queryKey: ['planificadorCentro', Number(centroId), fecha],
                });
                queryClient.invalidateQueries({
                  predicate: (q) => String(q.queryKey?.[0] ?? '').includes('planificador'),
                });
              }
              queryClient.invalidateQueries({ queryKey: ['reserva', id] });
            } catch {}
            notifyReservaUpdated(id);

            enqueueSnackbar(t('aulas.autoAssign.ok', { defaultValue: 'Aula auto-asignada' }), {
              variant: 'success',
            });
          } catch (e) {
            if (n < 3 && shouldRetryLock(e)) {
              attempt(n + 1);
              return;
            }
            enqueueSnackbar(
              t('aulas.autoAssign.fail', {
                defaultValue:
                  'No se pudo auto-asignar el aula ahora. Puedes reintentar desde el planner.',
              }),
              { variant: 'warning' },
            );
          }
        }, delay);
      };

      attempt(0);
    },
    [autoAssign, enqueueSnackbar, t, watch, autoAssignOnSave, queryClient, notifyReservaUpdated],
  );

  // Utilidad: obtener id de reserva del response (robusto a varias formas)
  const extractReservaIdFromCreate = (created: any): number | undefined => {
    const c = created ?? {};

    // Casos directos
    const direct =
      Number(c?.id) ||
      Number(c?.reservaId) ||
      Number(c?.firstId) ||
      Number(c?.primerId) ||
      Number(c?.data?.id) ||
      Number(c?.data?.reservaId) ||
      Number(c?.data?.firstId) ||
      Number(c?.data?.primerId);
    if (Number.isFinite(direct) && direct) return direct;

    // Variantes tipo insertId
    const insertLike = Number(c?.insertId) || Number(c?.data?.insertId);
    if (Number.isFinite(insertLike) && insertLike) return insertLike;

    // Arrays de ids o filas
    const idsArr =
      (Array.isArray(c?.ids) && c.ids) ||
      (Array.isArray(c?.data?.ids) && c.data.ids) ||
      (Array.isArray(c?.rows) && c.rows) ||
      (Array.isArray(c?.data?.rows) && c.data.rows);
    if (idsArr?.length) {
      const first = Number(idsArr[0]?.id ?? idsArr[0]);
      if (Number.isFinite(first) && first) return first;
    }

    // Objetos anidados frecuentes
    const nested =
      Number(c?.reserva?.id) ||
      Number(c?.item?.id) ||
      Number(c?.row?.id) ||
      Number(c?.data?.reserva?.id) ||
      Number(c?.data?.item?.id) ||
      Number(c?.data?.row?.id);
    if (Number.isFinite(nested) && nested) return nested;

    return undefined;
  };

  const onSubmit = useCallback(
    async (data: ReservationFormData & { scopeSeries: string }) => {
      let modeApi: 'create' | 'edit' | 'recurrente' | 'serieUpdate';
      if (data.recurre) {
        modeApi = 'recurrente';
      } else if (mode === 'edit' && (initialValues as any).idRecurrencia) {
        modeApi = scopeSeries === 'current' ? 'edit' : 'serieUpdate';
      } else {
        modeApi = mode === 'edit' ? 'edit' : 'create';
      }

      const { alumnosSel, esGrupal, numAlumnos, incidencia, comentariosInternos, ...rest } =
        data as any;

      const estadoSel: string | undefined = rest?.estado;
      if ('estado' in rest) delete rest.estado;

      const requiereAlumnos = modeApi !== 'serieUpdate' && !asignNoRequiere;
      if (requiereAlumnos && (!Array.isArray(alumnosSel) || alumnosSel.length === 0)) {
        enqueueSnackbar(
          t('reservationForm.error.selectStudent', {
            defaultValue: 'Selecciona al menos un alumno',
          }),
          { variant: 'error' },
        );
        return;
      }

      const payload: any = { incidencia };

      const applyEstadoTo = (target: any) => {
        if (!estadoSel) return;
        if (looksLikeCode(estadoSel)) {
          target.estadoCode = estadoSel;
          if ('estado' in target) delete target.estado;
        } else {
          target.estado = estadoSel;
          if ('estadoCode' in target) delete target.estadoCode;
        }
      };

      const esSerieCancel =
        modeApi === 'serieUpdate' &&
        !!estadoSel &&
        (ESTADOS_CANCELLED as readonly string[]).includes(estadoSel as any);

      if (esSerieCancel) {
        applyEstadoTo(payload);
        if (showInternos) payload.comentariosInternos = comentariosInternos?.trim() || undefined;
      } else {
        if (alumnosSel && alumnosSel[0] != null) {
          payload.idClienteUsr = alumnosSel[0];
        } else if (modeApi === 'edit' && asignNoRequiere) {
          payload.idClienteUsr = null;
        }
        // Si la asignatura permite no requerir alumno y no hay seleccionado,
        // no incluimos idClienteUsr en create/serieUpdate.

        // Limpiar campos del rest antes de asignar
        const restClean = { ...rest } as any;
        // Eliminar estado del rest (se maneja por separado)
        if ('estado' in restClean) delete restClean.estado;
        // Eliminar recurre del rest solo si no es modo recurrente
        if (modeApi !== 'recurrente' && 'recurre' in restClean) delete restClean.recurre;
        Object.assign(payload, restClean);

        if (showInternos) {
          payload.comentariosInternos = comentariosInternos?.trim() || undefined;
        }

        if (esGrupal) {
          payload.numAlumnos = numAlumnos;
          payload.alumnosIds = alumnosSel.slice(1);
        } else {
          payload.numAlumnos = 1;
          payload.alumnosIds = [];
        }

        // Aplicar estado en edición individual y edición de serie (no en creación).
        if (modeApi === 'edit' || modeApi === 'serieUpdate') {
          applyEstadoTo(payload);
        }
      }

      if (modeApi === 'edit') delete payload.idRecurrencia;

      // Eliminar recurre también para create (solo debe estar en recurrentes)
      if (modeApi === 'create' || modeApi === 'edit') {
        delete payload.recurre;
      }

      if (modeApi === 'serieUpdate') {
        delete payload.fecha;
        delete payload.recurre;
        delete payload.patron;
        delete payload.diasSemana;
        delete payload.intervalo;
        delete payload.repeticiones;
      }

      let desdeParam: string | undefined;
      if (modeApi === 'serieUpdate') {
        desdeParam =
          scopeSeries === 'future'
            ? ((initialValues as any).fecha?.slice(0, 10) ?? dayjs().format('YYYY-MM-DD'))
            : '1900-01-01';
      }

      try {
        // snapshot del intento para reintento manual asistido
        lastAttemptRef.current = payload;

        // Incluir shadowing si procede
        payload.shadowing = Boolean((data as any).shadowing);
        if ((data as any).shadowing) payload.idProfesorShadow = (data as any).idProfesorShadow;

        const created = await mutateAsync({
          mode: modeApi,
          id: modeApi === 'edit' ? reservaId : undefined,
          serieId: modeApi === 'serieUpdate' ? (initialValues as any).idRecurrencia : undefined,
          desde: modeApi === 'serieUpdate' ? desdeParam : undefined,
          data: payload,
        });

        /* ╔══════════════════════════════════════════════════════════╗
           ║  ASIGNACIÓN MANUAL (opcional)                            ║
           ╚══════════════════════════════════════════════════════════╝ */
        const aulaElegida =
          selectedAulaId && typeof selectedAulaId === 'number' ? selectedAulaId : null;

        // Para decidir a qué reserva aplicar:
        const reservaIdForOps =
          modeApi === 'edit' || modeApi === 'serieUpdate'
            ? (reservaId as number | undefined)
            : extractReservaIdFromCreate(created);

        // Construye el body de propagación según alcance (sin checkbox; usa scopeSeries)
        const buildPropagarBody = () => {
          // EDITAR SERIE: respeta las casillas "Esta / Siguientes / Todas"
          if (modeApi === 'serieUpdate') {
            if (scopeSeries === 'all') return { scope: 'RECURRENCIA' as const };
            if (scopeSeries === 'future') {
              const d = ((initialValues as any)?.fecha?.slice(0, 10) ||
                (watch('fecha') as string | undefined) ||
                dayjs().format('YYYY-MM-DD')) as string;
              return { scope: 'RANGO' as const, desde: d, hasta };
            }
            return null;
          }

          // CREAR SERIE: mantenemos comportamiento previo (aplicar a toda la recurrencia)
          if (modeApi === 'recurrente') {
            return { scope: 'RECURRENCIA' as const };
          }

          // EDIT (instancia suelta) o CREATE simple → solo la actual
          return null;
        };

        if (aulaElegida && reservaIdForOps) {
          try {
            const propBody = buildPropagarBody();

            if (propBody) {
              await asignManualProp.mutateAsync({
                reservaId: reservaIdForOps,
                aulaId: aulaElegida,
                body: propBody,
              });

              /* ⭐️ invalidar caches + avisar diálogo */
              try {
                queryClient.invalidateQueries({
                  queryKey: ['planificadorCentro', Number(centroIdSel), fechaSel],
                });
                queryClient.invalidateQueries({
                  predicate: (q) => String(q.queryKey?.[0] ?? '').includes('planificador'),
                });
                queryClient.invalidateQueries({ queryKey: ['reserva', reservaIdForOps] });
              } catch {}
              notifyReservaUpdated(reservaIdForOps);

              enqueueSnackbar(
                t('aulas.manualAssign.seriesOk', {
                  defaultValue:
                    'Aula aplicada según el alcance seleccionado (con reorganización si hiciera falta).',
                }),
                { variant: 'success' },
              );
            } else {
              await asignManual.mutateAsync({
                reservaId: reservaIdForOps,
                aulaId: aulaElegida,
              });

              /* ⭐️ invalidar caches + avisar diálogo */
              try {
                queryClient.invalidateQueries({
                  queryKey: ['planificadorCentro', Number(centroIdSel), fechaSel],
                });
                queryClient.invalidateQueries({
                  predicate: (q) => String(q.queryKey?.[0] ?? '').includes('planificador'),
                });
                queryClient.invalidateQueries({ queryKey: ['reserva', reservaIdForOps] });
              } catch {}
              notifyReservaUpdated(reservaIdForOps);

              enqueueSnackbar(
                t('aulas.manualAssign.ok', {
                  defaultValue: 'Aula asignada (se reorganizarán aulas si estuviera ocupada).',
                }),
                { variant: 'success' },
              );
            }
          } catch (e) {
            // No bloqueamos el guardado si la asignación manual falla
            const msg =
              (isAxiosError(e) && (e.response?.data as any)?.message) ||
              t('aulas.manualAssign.fail', {
                defaultValue: 'No se pudo aplicar la asignación manual.',
              });
            enqueueSnackbar(String(msg), { variant: 'warning' });
          }
        } else {
          // Si no hay aula elegida, mantenemos la auto-asignación opcional
          if (!aulaElegida) {
            let reservaIdForAuto: number | undefined;
            if (modeApi === 'create') {
              reservaIdForAuto =
                (created as any)?.id ??
                (created as any)?.reservaId ??
                (created as any)?.data?.id ??
                (created as any)?.data?.reservaId ??
                extractReservaIdFromCreate(created);
            } else if (modeApi === 'edit') {
              reservaIdForAuto = reservaId;
            }
            if (reservaIdForAuto) {
              runAutoAssignWithBackoff(reservaIdForAuto);
            }
          }
        }
        /* ═══════════════════════════════════════════════════════════ */

        onSaved?.();
      } catch (err) {
        if (isInactiveTeacherError(err)) {
          enqueueSnackbar(
            t('reservationForm.error.inactiveTeacher', {
              defaultValue: 'No se puede guardar en Confirmed: el profesor está inactivo.',
            }),
            { variant: 'error' },
          );
          return;
        }

        if (isWeeklyLimitError(err)) {
          if (!SAD_FEATURE_ENABLED) {
            enqueueSnackbar('SAD deshabilitado temporalmente.', { variant: 'info' });
            return;
          }
          try {
            const ownerId = (user as any)?.id as number | undefined;
            const profesorUserId =
              (err as any)?.response?.data?.data?.profesorUserId ??
              lastAttemptRef.current?.idProfesor ??
              (watch('idProfesor') as number | undefined);

            const proyectoId =
              (err as any)?.response?.data?.data?.proyectoId ??
              lastAttemptRef.current?.idProyecto ??
              (watch('idProyecto') as number | undefined);

            if (ownerId && profesorUserId && lastAttemptRef.current) {
              saveSadRetryAttempt(ownerId, profesorUserId, proyectoId, lastAttemptRef.current);
            }
          } catch {}

          setSadHorasInitial({
            profesorUserId:
              (err as any)?.response?.data?.data?.profesorUserId || (watch('idProfesor') as number),
            semanaLunes: (err as any)?.response?.data?.data?.semanaLunes,
            horasSolicitadas: (err as any)?.response?.data?.data?.sugeridoHoras,
            proyectoId: (watch('idProyecto') as number) || undefined,
          });
          setSadHorasOpen(true);
          return;
        }

        if (isNoAvailabilityError(err)) {
          if (!SAD_FEATURE_ENABLED) {
            enqueueSnackbar('SAD deshabilitado temporalmente.', { variant: 'info' });
            return;
          }
          try {
            const ownerId = (user as any)?.id as number | undefined;
            const profesorUserId =
              (err as any)?.response?.data?.data?.profesorUserId ??
              lastAttemptRef.current?.idProfesor ??
              (watch('idProfesor') as number | undefined);

            const proyectoId =
              (err as any)?.response?.data?.data?.proyectoId ??
              lastAttemptRef.current?.idProyecto ??
              (watch('idProyecto') as number | undefined);

            if (ownerId && profesorUserId && lastAttemptRef.current) {
              saveSadRetryAttempt(ownerId, profesorUserId!, proyectoId, lastAttemptRef.current);
            }
          } catch {}

          // Si la recurrencia supera 14 días, no ofrecer SAD
          const fechaInicio = watch('fecha') as string;
          const fechaFin = watch('fecha') as string;
          if (recurrenceExceedsTwoWeeks(fechaInicio, fechaFin)) {
            enqueueSnackbar(
              t('reservationForm.error.rangeTooLong', {
                defaultValue: 'La recurrencia supera 14 días; no se ofrece SAD.',
              }),
              {
                variant: 'info',
              },
            );
            return;
          }

          // Abrir diálogo SAD de disponibilidad puntual (prefill con el tramo)
          setSadDispInitial({
            profesorUserId: (watch('idProfesor') as number)!,
            proyectoId: watch('idProyecto') as number,
            asignaturaId: watch('idAsignatura') as number | undefined,
            formatoId: watch('idFormato') as number | undefined,
            centroId: watch('idCentro') as number | undefined,
            idioma: watch('language') as string | undefined,
            tipo: 'puntual',
            fechasPuntuales: [
              {
                fecha: watch('fecha') as string,
                horaInicio: watch('horaInicio') as string,
                horaFin: watch('horaFin') as string,
              },
            ],
            periodoFin: watch('fecha') as string,
          });
          setSadDispOpen(true);
          return;
        }

        // Límite de pendientes del 30% en series
        if (isPendingLimitError(err)) {
          if (!SAD_FEATURE_ENABLED) {
            enqueueSnackbar('SAD deshabilitado temporalmente.', { variant: 'info' });
            return;
          }
          try {
            const ownerId = (user as any)?.id as number | undefined;
            const profesorUserId =
              (err as any)?.response?.data?.data?.profesorUserId ??
              lastAttemptRef.current?.idProfesor ??
              (watch('idProfesor') as number | undefined);
            const proyectoId =
              (err as any)?.response?.data?.data?.proyectoId ??
              lastAttemptRef.current?.idProyecto ??
              (watch('idProyecto') as number | undefined);

            if (ownerId && profesorUserId && lastAttemptRef.current) {
              saveSadRetryAttempt(ownerId, profesorUserId!, proyectoId, lastAttemptRef.current);
            }
          } catch {}

          const d = (err as any)?.response?.data?.data ?? {};

          const periodoInicio = d.periodoInicio ?? (watch('fecha') as string | undefined);
          const periodoFin =
            d.periodoFin ??
            (() => {
              const base = dayjs(watch('fecha') as string);
              const rep = (watch('repeticiones') as number) || 1;
              const intv = (watch('intervalo') as number) || 1;
              const patt = watch('patron') as string;
              if (patt === 'DAILY5')
                return base.add(Math.min(rep, 5) - 1, 'day').format('YYYY-MM-DD');
              if (patt === 'MONTHLY')
                return base.add(intv * (rep - 1), 'month').format('YYYY-MM-DD');
              return base.add(intv * (rep - 1), 'week').format('YYYY-MM-DD');
            })();

          if (recurrenceExceedsTwoWeeks(periodoInicio, periodoFin)) {
            enqueueSnackbar(
              t('reservationForm.error.rangeTooLong', {
                defaultValue: 'La recurrencia supera 14 días; no se ofrece SAD.',
              }),
              { variant: 'info' },
            );
            return;
          }
          // Abrir SAD PERIÓDICO con patrón y rango
          setSadDispInitial({
            profesorUserId: d.profesorUserId ?? (watch('idProfesor') as number),
            proyectoId: d.proyectoId ?? (watch('idProyecto') as number),
            asignaturaId: d.asignaturaId ?? (watch('idAsignatura') as number | undefined),
            formatoId: d.formatoId ?? (watch('idFormato') as number | undefined),
            centroId: d.centroId ?? (watch('idCentro') as number | undefined),
            idioma: d.idioma ?? (watch('language') as string | undefined),
            tipo: 'periodico',
            patron: d.patron ?? (watch('patron') as any),
            diasSemana: d.diasSemana ?? (watch('diasSemana') as number[] | undefined),
            intervalo: d.intervalo ?? (watch('intervalo') as number | undefined),
            horaInicio: d.horaInicio ?? (watch('horaInicio') as string),
            horaFin: d.horaFin ?? (watch('horaFin') as string),
            periodoInicio: d.periodoInicio ?? (watch('fecha') as string),
            periodoFin:
              d.periodoFin ??
              (() => {
                const base = dayjs(watch('fecha') as string);
                const rep = (watch('repeticiones') as number) || 1;
                const intv = (watch('intervalo') as number) || 1;
                const patt = watch('patron') as string;
                if (patt === 'DAILY5')
                  return base.add(Math.min(rep, 5) - 1, 'day').format('YYYY-MM-DD');
                if (patt === 'MONTHLY')
                  return base.add(intv * (rep - 1), 'month').format('YYYY-MM-DD');
                return base.add(intv * (rep - 1), 'week').format('YYYY-MM-DD');
              })(),
          } as any);
          setSadDispOpen(true);
          return;
        }

        // Error genérico
        let msg = t('reservationForm.error.unexpected', {
          defaultValue: 'Error inesperado',
        });
        if (isAxiosError(err)) {
          const m = (err.response?.data as any)?.message;
          msg = Array.isArray(m) ? m.join(' · ') : (m ?? msg);
        }
        enqueueSnackbar(msg, { variant: 'error' });
      }
    },
    [
      mode,
      reservaId,
      mutateAsync,
      initialValues,
      onSaved,
      showInternos,
      enqueueSnackbar,
      scopeSeries,
      t,
      watch,
      user,
      runAutoAssignWithBackoff,
      selectedAulaId,
      asignManual,
      asignManualProp,
      hasta,
      centroIdSel,
      fechaSel,
      notifyReservaUpdated,
      queryClient,
    ],
  );

  /* ── 6. loader ── */
  if (loadingCats) return <CircularProgress sx={{ my: 4 }} />;

  /* ── 7. UI ── */
  const isCancelledSelected =
    !!watch('estado') && (ESTADOS_CANCELLED as readonly string[]).includes(watch('estado') as any);

  return (
    <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
      <Grid container spacing={2}>
        {/* ——— clase grupal ——— */}
        <Grid item xs={12}>
          <Controller
            name="esGrupal"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label={t('reservationForm.field.esGrupal')}
              />
            )}
          />
        </Grid>

        {/* (Profesor moved below Asignatura) */}

        {/* ——— alumnos ——— */}
        <Grid item xs={12}>
          <Controller
            name="alumnosSel"
            control={control}
            render={({ field, fieldState }) => {
              const valueArr = Array.isArray(field.value) ? field.value : [];
              const seleccionados = clientes.filter((c) => valueArr.includes(c.id));

              return (
                <Stack direction="row" spacing={1}>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    sx={{ flexGrow: 1 }}
                    options={clientes}
                    getOptionLabel={(o) => o?.nombreCompleto || o?.email || `#${o?.id}`}
                    value={seleccionados}
                    onChange={(_, v) => {
                      const vFiltrado = v.filter(
                        (u) => u.rol === 'cliente' || valueArr.includes(u.id),
                      );
                      if (!watch('esGrupal') && vFiltrado.length > 1) return;
                      if (watch('esGrupal') && vFiltrado.length > watch('numAlumnos')) return;
                      field.onChange(vFiltrado.map((u) => u.id));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('reservationForm.field.alumnos')}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />

                  <Tooltip title={t('reservationForm.newClient')}>
                    <IconButton edge="end" size="large" onClick={() => setClienteDlgOpen(true)}>
                      <PersonAddIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              );
            }}
          />
        </Grid>

        {/* ——— plazas totales ——— */}
        {watch('esGrupal') && (
          <Grid item xs={12} sm={4}>
            <Controller
              name="numAlumnos"
              control={control}
              rules={{
                min: { value: 1, message: t('reservationForm.error.minOne') },
                validate: (v) =>
                  (watch('alumnosSel') as number[]).length <= v ||
                  t<string>('reservationForm.error.alreadySelected', {
                    count: (watch('alumnosSel') as number[]).length,
                  }),
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  type="number"
                  fullWidth
                  label={t('reservationForm.field.numAlumnos')}
                  inputProps={{ min: 1 }}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
        )}

        {/* ——— Asignatura (autocomplete filtrable) ——— */}
        <Grid item xs={12}>
          <Controller
            name="idAsignatura"
            control={control}
            render={({ field, fieldState }) => {
              const selected =
                asignaturas.find((a) => Number(a.id) === Number(field.value)) ?? null;
              return (
                <Autocomplete
                  fullWidth
                  options={asignaturas}
                  getOptionLabel={(o: any) => (o ? o.nombre : '')}
                  value={selected}
                  onChange={(_, v) => field.onChange(v ? Number(v.id) : (undefined as any))}
                  filterOptions={(opts, state) =>
                    opts.filter((o: any) =>
                      (o.nombre ?? '')
                        .toLowerCase()
                        .includes((state.inputValue ?? '').toLowerCase()),
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('reservationForm.field.asignatura')}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              );
            }}
          />
        </Grid>
        {/* ——— Profesor + Shadowing (misma fila, comportamiento similar a alumnos) ——— */}
        <Grid item xs={12}>
          <Stack direction="row" spacing={1}>
            <Autocomplete
              multiple={Boolean(watch('shadowing'))}
              disableCloseOnSelect={Boolean(watch('shadowing'))}
              sx={{ flexGrow: 1 }}
              options={profesores}
              getOptionLabel={(o: any) => o?.nombre || `#${o?.id}`}
              value={
                watch('shadowing')
                  ? profesores.filter((p) =>
                      [watch('idProfesor'), watch('idProfesorShadow')].includes(p.id),
                    )
                  : (profesores.find((p) => Number(p.id) === Number(watch('idProfesor'))) ?? null)
              }
              onChange={(_, v) => {
                const isShadow = Boolean(watch('shadowing'));
                if (isShadow) {
                  const sel = Array.isArray(v) ? v : [];
                  const sliced = sel.slice(0, 2);
                  const ids = sliced.map((s: any) => Number(s.id));
                  setValue('idProfesor', (ids[0] ?? undefined) as any, { shouldDirty: true });
                  setValue('idProfesorShadow', (ids[1] ?? undefined) as any, { shouldDirty: true });
                } else {
                  const sel = v as any;
                  setValue('idProfesor', (sel ? Number(sel.id) : undefined) as any, {
                    shouldDirty: true,
                  });
                  setValue('idProfesorShadow', undefined as any, { shouldDirty: true });
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('reservationForm.field.profesor')}
                  placeholder={watch('shadowing') ? 'Selecciona hasta 2 profesores' : undefined}
                />
              )}
            />

            <Controller
              name="shadowing"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={Boolean(field.value)}
                      onChange={(_, v) => {
                        field.onChange(v);
                        if (!v) {
                          setValue('idProfesorShadow', undefined, { shouldDirty: true });
                        }
                      }}
                    />
                  }
                  label="Shadowing"
                />
              )}
            />
          </Stack>
        </Grid>
        {/* ——— Roles: elegir profesor principal cuando hay shadowing ——— */}
        {watch('shadowing') && (
          <Grid item xs={12}>
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend">Profesor/a principal</FormLabel>
              <RadioGroup
                row
                value={String(watch('idProfesor') ?? '')}
                onChange={(e) => {
                  const newMain = Number(e.target.value);
                  const selected = profesores.filter((p) =>
                    [watch('idProfesor'), watch('idProfesorShadow')].includes(p.id),
                  );
                  const other = selected.find((p) => p.id !== newMain);
                  setValue('idProfesor', (newMain ?? undefined) as any, { shouldDirty: true });
                  setValue('idProfesorShadow', (other ? (other.id as any) : undefined) as any, {
                    shouldDirty: true,
                  });
                }}
              >
                {profesores
                  .filter((p) => [watch('idProfesor'), watch('idProfesorShadow')].includes(p.id))
                  .map((p) => (
                    <FormControlLabel
                      key={p.id}
                      value={String(p.id)}
                      control={<Radio />}
                      label={p.nombre}
                    />
                  ))}
              </RadioGroup>
            </FormControl>
          </Grid>
        )}

        {/* ——— Formato ——— */}
        <Grid item xs={12}>
          <FormControl fullWidth disabled={loadingFmt}>
            <InputLabel id="fmt">{t('reservationForm.field.formato')}</InputLabel>
            <Select
              labelId="fmt"
              label={t('reservationForm.field.formato')}
              value={watch('idFormato') ?? ''}
              onChange={(e) =>
                setValue('idFormato', Number(e.target.value), {
                  shouldDirty: true,
                })
              }
            >
              {formatosVisibles.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.nombreFormato}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* ——— Centro ——— */}
        <Grid item xs={12}>
          <FormControl fullWidth disabled={loadingCtr}>
            <InputLabel id="ctr">{t('reservationForm.field.centro')}</InputLabel>
            <Select
              labelId="ctr"
              label={t('reservationForm.field.centro')}
              value={watch('idCentro') ?? ''}
              onChange={(e) =>
                setValue('idCentro', Number(e.target.value) || undefined, {
                  shouldDirty: true,
                })
              }
            >
              {centrosVisibles.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* ——— Preferencia: Auto-asignar al guardar ——— */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox checked={autoAssignOnSave} onChange={(_, v) => setAutoAssignOnSave(v)} />
            }
            label={t('reservationForm.field.autoAssignOnSave', {
              defaultValue: 'Auto-asignar aula al guardar',
            })}
            disabled={!watch('idCentro')}
          />
        </Grid>

        {/* ——— Selector de Aula (manual) ——— */}
        <Grid item xs={12}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
            <FormControl fullWidth disabled={!centroIdSel || aulasLoading || planLoading}>
              <InputLabel id="aulaSel">
                {t('reservationForm.field.aula', {
                  defaultValue: 'Aula (manual, opcional)',
                })}
              </InputLabel>
              <Select
                labelId="aulaSel"
                label={t('reservationForm.field.aula', {
                  defaultValue: 'Aula (manual, opcional)',
                })}
                value={selectedAulaId === '' ? '' : Number(selectedAulaId)}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setSelectedAulaId(Number.isFinite(v) ? v : '');
                }}
                renderValue={(v) => {
                  const id = typeof v === 'number' ? v : NaN;
                  const a = aulasCentro.find((x: any) => x.id === id);
                  return a
                    ? `${a.nombre} (${a.capacidad})`
                    : t('reservationForm.field.selAula', {
                        defaultValue: 'Selecciona un aula',
                      });
                }}
              >
                <MenuItem value="">
                  <em>
                    {t('reservationForm.field.selAula', {
                      defaultValue: 'Selecciona un aula',
                    })}
                  </em>
                </MenuItem>
                {aulasCentro.map((a: any) => {
                  const info = infoForAula(a.id);
                  const capInsuf = a.capacidad < Math.max(1, (watch('numAlumnos') as number) || 1);
                  const isBlocked = bloqueadasSet.has(a.id);
                  return (
                    <MenuItem key={a.id} value={a.id} disabled={capInsuf || isBlocked}>
                      <Tooltip
                        title={
                          capInsuf
                            ? t('aulas.info.capacidad', {
                                defaultValue: 'Capacidad insuficiente',
                              })
                            : (info ?? '')
                        }
                      >
                        <span>
                          {a.nombre} · {a.capacidad}
                          {info ? ` — ${info}` : ''}
                        </span>
                      </Tooltip>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Stack>
          {selectedAulaId && (
            <Typography variant="caption" color="text.secondary">
              {t('aulas.info.fallback', {
                defaultValue:
                  'Si el aula elegida está ocupada, el sistema reorganizará automáticamente otras aulas para liberarla. Las aulas bloqueadas no se pueden seleccionar.',
              })}
            </Typography>
          )}
        </Grid>

        {/* ——— Estado (solo edición) ——— */}
        {mode === 'edit' && (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="sts">{t('reservationForm.field.estado')}</InputLabel>
              <Select
                labelId="sts"
                label={t('reservationForm.field.estado')}
                value={watch('estado') ?? ''}
                onChange={(e) =>
                  setValue('estado', e.target.value as any, {
                    shouldDirty: true,
                  })
                }
              >
                {(ESTADOS_RESERVA as readonly string[]).map((st) => (
                  <MenuItem key={st} value={st}>
                    {st}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {isCancelledSelected && (
              <Typography variant="caption" color="text.secondary">
                {t('reservationForm.msg.rememberExplainCancel')}
              </Typography>
            )}
          </Grid>
        )}

        {/* ——— Fecha & Horas ——— */}
        <Grid item xs={4}>
          <TextField
            type="date"
            fullWidth
            label={t('reservationForm.field.fecha')}
            disabled={scopeSeries !== 'current'}
            InputLabelProps={{ shrink: true }}
            value={watch('fecha') ?? ''}
            onChange={(e) => setValue('fecha', e.target.value, { shouldDirty: true })}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            type="time"
            fullWidth
            label={t('reservationForm.field.horaInicio')}
            InputLabelProps={{ shrink: true }}
            value={watch('horaInicio') ?? ''}
            onChange={(e) => setValue('horaInicio', e.target.value, { shouldDirty: true })}
            inputProps={{ max: watch('horaFin') ?? undefined }}
            error={!!(errors as any)?.horaInicio}
            helperText={(errors as any)?.horaInicio?.message as string}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            type="time"
            fullWidth
            label={t('reservationForm.field.horaFin')}
            InputLabelProps={{ shrink: true }}
            value={watch('horaFin') ?? ''}
            onChange={(e) => setValue('horaFin', e.target.value, { shouldDirty: true })}
            inputProps={{ min: watch('horaInicio') ?? undefined }}
            error={!!(errors as any)?.horaFin}
            helperText={(errors as any)?.horaFin?.message as string}
          />
        </Grid>

        {/* ——— Alcance de edición para series ——— */}
        {mode === 'edit' && (initialValues as any).idRecurrencia && (
          <Grid item xs={12}>
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend">{t('reservationForm.series.label')}</FormLabel>
              <Controller
                name="scopeSeries"
                control={control}
                render={({ field }) => (
                  <RadioGroup row {...field}>
                    <FormControlLabel
                      value="current"
                      control={<Radio />}
                      label={t('reservationForm.series.current')}
                    />
                    <FormControlLabel
                      value="future"
                      control={<Radio />}
                      label={t('reservationForm.series.future')}
                    />
                    <FormControlLabel
                      value="all"
                      control={<Radio />}
                      label={t('reservationForm.series.all')}
                    />
                  </RadioGroup>
                )}
              />
            </FormControl>
          </Grid>
        )}

        {/* ——— Reserva recurrente ——— */}
        <Grid item xs={12}>
          <Controller
            name="recurre"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={Boolean(field.value)} />}
                label={t('reservationForm.recurrence.label')}
              />
            )}
          />
        </Grid>

        {/* ——— Config recurrencia ——— */}
        {watch('recurre') && (
          <>
            <Grid item xs={12} sm={6}>
              <Controller
                name="patron"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    label={t('reservationForm.recurrence.patternLabel')}
                  >
                    <MenuItem value="WEEKLY">{t('reservationForm.recurrence.weekly')}</MenuItem>
                    <MenuItem value="MONTHLY">{t('reservationForm.recurrence.monthly')}</MenuItem>
                    <MenuItem value="DAILY5">{t('reservationForm.recurrence.daily5')}</MenuItem>
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="intervalo"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    fullWidth
                    label={t('reservationForm.recurrence.intervalLabel')}
                    inputProps={{ min: 1 }}
                  />
                )}
              />
            </Grid>

            {watch('patron') === 'WEEKLY' && (
              <Grid item xs={12}>
                <Controller
                  name="diasSemana"
                  control={control}
                  render={({ field }) => (
                    <FormGroup row>
                      {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map((d, i) => (
                        <FormControlLabel
                          key={d}
                          label={d}
                          control={
                            <Checkbox
                              checked={field.value?.includes(i) || false}
                              onChange={(_, v) => {
                                const s = new Set(field.value ?? []);
                                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                                v ? s.add(i) : s.delete(i);
                                field.onChange([...s]);
                              }}
                            />
                          }
                        />
                      ))}
                    </FormGroup>
                  )}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <Controller
                name="repeticiones"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    fullWidth
                    label={t('reservationForm.recurrence.repetitionsLabel')}
                    inputProps={{ min: 1 }}
                  />
                )}
              />
            </Grid>
          </>
        )}

        {/* ——— Proyecto + Idioma ——— */}
        <Grid item xs={8}>
          <FormControl fullWidth>
            <InputLabel id="proy">{t('reservationForm.field.proyecto')}</InputLabel>
            <Select
              labelId="proy"
              label={t('reservationForm.field.proyecto')}
              value={watch('idProyecto') ?? ''}
              onChange={(e) =>
                setValue('idProyecto', Number(e.target.value), {
                  shouldDirty: true,
                })
              }
            >
              {proyectos.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={4}>
          <FormControl fullWidth>
            <InputLabel id="lang">{t('reservationForm.field.idioma')}</InputLabel>
            <Select
              labelId="lang"
              label={t('reservationForm.field.idioma')}
              value={watch('language')}
              onChange={(e) =>
                setValue('language', e.target.value as any, {
                  shouldDirty: true,
                })
              }
            >
              <MenuItem value="English">English</MenuItem>
              <MenuItem value="Spanish">Spanish</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* ——— Curso + Board ——— */}
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel id="curso">{t('reservationForm.field.curso')}</InputLabel>
            <Select
              labelId="curso"
              label={t('reservationForm.field.curso')}
              value={watch('curso') ?? ''}
              onChange={(e) =>
                setValue('curso', (e.target.value as (typeof CURSOS)[number]) || undefined, {
                  shouldDirty: true,
                })
              }
            >
              <MenuItem value="">
                <em>{t('reservationForm.field.noCurso')}</em>
              </MenuItem>
              {CURSOS.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label={t('reservationForm.field.board')}
            value={watch('examinationBoard') ?? ''}
            onChange={(e) =>
              setValue('examinationBoard', e.target.value, {
                shouldDirty: true,
              })
            }
          />
        </Grid>

        {/* ——— Link + Comentarios públicos ——— */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('reservationForm.field.link')}
            value={watch('linkAddress') ?? ''}
            onChange={(e) => setValue('linkAddress', e.target.value, { shouldDirty: true })}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('reservationForm.field.comentarios')}
            value={watch('comentarios') ?? ''}
            onChange={(e) => setValue('comentarios', e.target.value, { shouldDirty: true })}
          />
        </Grid>

        {/* ——— Comentarios internos (solo staff) ——— */}
        {!(isProfesor || isCliente) && (
          <Grid item xs={12}>
            <Controller
              name="comentariosInternos"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  rows={3}
                  label={t('reservationForm.field.comentariosInternos')}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
        )}

        {/* ——— incidencia ——— */}
        <Grid item xs={12}>
          <Controller
            name="incidencia"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={Boolean(field.value)} />}
                label={t('reservationForm.field.incidencia')}
              />
            )}
          />
          {watch('incidencia') && !(isProfesor || isCliente) && (
            <Typography variant="caption" color="text.secondary">
              {t('reservationForm.msg.rememberDescribeIssue')}
            </Typography>
          )}
        </Grid>

        {/* ——— Botones ——— */}
        <Grid item xs={12}>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <LoadingButton onClick={onCancel}>{t('common.cancel')}</LoadingButton>
            <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
              {t('common.save')}
            </LoadingButton>
          </Stack>
        </Grid>
      </Grid>

      {/* ClienteDialog con Paper arrastrable */}
      <ClienteDialog
        open={clienteDlgOpen}
        onClose={() => setClienteDlgOpen(false)}
        onSave={async (dto) => {
          await api.post('/clientes', dto);
          await reloadUsuarios();
          setClienteDlgOpen(false);
        }}
        keepMounted
        PaperComponent={DraggablePaper}
        PaperProps={{
          sx: {
            resize: 'both',
            overflow: 'auto',
            maxWidth: '90vw',
            maxHeight: '90vh',
          },
        }}
      />

      {/* SAD Horas dialog */}
      {SAD_FEATURE_ENABLED && sadHorasOpen && sadHorasInitial && (
        <SadHorasDialog
          open={sadHorasOpen}
          onClose={() => setSadHorasOpen(false)}
          initial={sadHorasInitial}
        />
      )}

      {/* SAD Disponibilidad (puntual / periódico) */}
      {SAD_FEATURE_ENABLED && sadDispOpen && sadDispInitial && (
        <SadCreateDialog
          open={sadDispOpen}
          onClose={() => setSadDispOpen(false)}
          initial={sadDispInitial}
          onCreate={async (dto) => {
            const sad = await createSad({
              ...dto,
              profesorUserId: dto.profesorUserId,
              proyectoId: dto.proyectoId!,
            } as any);
            await sendSad(sad.id);
            enqueueSnackbar(
              t('sadPopup.toast.sentAvailability', {
                defaultValue: 'Solicitud de disponibilidad enviada al profesor',
              }),
              { variant: 'success' },
            );
          }}
        />
      )}
    </form>
  );
}
